import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import toast from "react-hot-toast";

import { BrandConstants } from "../Constants/BrandConstants";

export type Result<T, E = string> =
  | { ok: true; data: T }
  | { ok: false; error: E };

export type IO<T> = () => Promise<Result<T>>;

interface CacheEntryModel<T> {
  value: T;
  expiresAt: number;
}

export interface AgentRequestPayloadModel {
  intent: string;
  entities: {
    bucket: string;
    filename?: string;
    key?: string;
    content?: string;
    content_type?: string;
    data?: unknown;
  };
}

interface AgentResponseModel<T = unknown> {
  data: T | null;
  statusCode: number;
  raw?: unknown;
}

interface HttpRequestMetadataModel {
  start: number;
}

export interface HttpClientModel {
  get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: T; status: number }>;
  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<{ data: T; status: number }>;
}

const localCacheTtlMs = 5 * 60 * 1000;
const isBrowser = typeof window !== "undefined";

export const safeJsonParseFn = <T>(value: string | null): T | null => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

export const safeJsonStringifyFn = (value: unknown) => {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
};

export const setLocalCacheFn = <T>(
  key: string,
  value: T,
  ttlMs = localCacheTtlMs
) => {
  if (!isBrowser) {
    return;
  }

  const serialized = safeJsonStringifyFn({
    value,
    expiresAt: Date.now() + ttlMs,
  } satisfies CacheEntryModel<T>);

  if (!serialized) {
    return;
  }

  window.localStorage.setItem(key, serialized);
};

export const getLocalCacheFn = <T>(key: string): T | null => {
  if (!isBrowser) {
    return null;
  }

  const entry = safeJsonParseFn<CacheEntryModel<T>>(
    window.localStorage.getItem(key)
  );

  if (!entry) {
    return null;
  }

  if (entry.expiresAt <= Date.now()) {
    window.localStorage.removeItem(key);
    return null;
  }

  return entry.value;
};

export const clearLocalCacheFn = (key: string) => {
  if (isBrowser) {
    window.localStorage.removeItem(key);
  }
};

const appHttpClientInstance = axios.create({
  timeout: Number(import.meta.env.VITE_API_TIMEOUT_MS || "15000"),
  withCredentials: false,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

appHttpClientInstance.interceptors.request.use((config) => {
  const nextConfig = config;
  (nextConfig as AxiosRequestConfig & {
    metadata?: HttpRequestMetadataModel;
  }).metadata = { start: Date.now() };
  return nextConfig;
});

appHttpClientInstance.interceptors.response.use(
  (response) => {
    const metadata = (
      response.config as AxiosRequestConfig & {
        metadata?: HttpRequestMetadataModel;
      }
    ).metadata;

    if (metadata) {
      const elapsedMs = Date.now() - metadata.start;
      if (elapsedMs > 5000) {
        console.warn(
          `Slow request (${elapsedMs}ms): ${response.config.method?.toUpperCase()} ${response.config.url}`
        );
      }
    }

    return response;
  },
  (error) => {
    if (error.code === "ECONNABORTED") {
      toast("Request timed out", {
        duration: 4000,
        style: {
          backgroundColor: BrandConstants.colors.base,
          color: BrandConstants.colors.white,
        },
      });
    }

    return Promise.reject(error);
  }
);

export const appHttpClient: AxiosInstance = appHttpClientInstance;

const normalizeAgentBodyFn = <T>(body: unknown): T | null => {
  if (body && typeof body === "object") {
    const typedBody = body as {
      data?: T;
      result?: { data?: T } | T;
      success?: boolean;
      bucket?: string;
    };

    if (typedBody.result && typeof typedBody.result === "object") {
      const resultBody = typedBody.result as { data?: T };
      if ("data" in resultBody) {
        return resultBody.data ?? null;
      }

      return typedBody.result as T;
    }

    if (
      typedBody.data &&
      (!("success" in typedBody) || !("bucket" in typedBody))
    ) {
      return typedBody.data;
    }

    return typedBody as T;
  }

  return null;
};

export const getAgentPage = async <T>(page: string): Promise<AgentResponseModel<T>> => {
  const payload: AgentRequestPayloadModel = {
    intent: "do_retrieve_json_file",
    entities: {
      bucket: BrandConstants.bucketName,
      filename: `${page}.json`,
    },
  };

  try {
    const response = await appHttpClient.post("/agent", payload);
    return {
      data: normalizeAgentBodyFn<T>(response.data),
      statusCode: response.status,
      raw: response.data,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return {
        data: null,
        statusCode: error.response.status,
        raw: error.response.data,
      };
    }

    throw error;
  }
};

export const updateJsonForAgent = async <T>(
  page: string,
  data: T
): Promise<AgentResponseModel<T>> => {
  const payload: AgentRequestPayloadModel = {
    intent: "update_json_file",
    entities: {
      bucket: BrandConstants.bucketName,
      filename: `${page}.json`,
      data,
    },
  };

  try {
    const response = await appHttpClient.post("/agent", payload);
    return {
      data: normalizeAgentBodyFn<T>(response.data),
      statusCode: response.status,
      raw: response.data,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return {
        data: null,
        statusCode: error.response.status,
        raw: error.response.data,
      };
    }

    throw error;
  }
};

export const uploadForAgent = async <T>(
  payload: AgentRequestPayloadModel
): Promise<AgentResponseModel<T>> => {
  try {
    const response = await appHttpClient.post("/upload", payload);
    return {
      data: normalizeAgentBodyFn<T>(response.data),
      statusCode: response.status,
      raw: response.data,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return {
        data: null,
        statusCode: error.response.status,
        raw: error.response.data,
      };
    }

    throw error;
  }
};

const getPage = async <T>(page: string): Promise<
  Result<{
    data: T | null;
    source: "cache" | "agent" | "none";
    statusCode: number;
  }>
> => {
  const cacheKey = `${BrandConstants.bucketName}:${page}`;
  const cachedValue = getLocalCacheFn<T>(cacheKey);

  if (cachedValue) {
    return {
      ok: true,
      data: {
        data: cachedValue,
        source: "cache",
        statusCode: 200,
      },
    };
  }

  try {
    const response = await getAgentPage<T>(page);
    if (response.data) {
      setLocalCacheFn(cacheKey, response.data);
    }

    return {
      ok: true,
      data: {
        data: response.data,
        source: response.data ? "agent" : "none",
        statusCode: response.statusCode,
      },
    };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Unable to load page",
    };
  }
};

const updatePage = async <T>(page: string, data: T): Promise<Result<T>> => {
  try {
    const response = await updateJsonForAgent<T>(page, data);
    setLocalCacheFn(`${BrandConstants.bucketName}:${page}`, data);
    return {
      ok: true,
      data: response.data ?? data,
    };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Unable to update page",
    };
  }
};

export const AppService = {
  cache: setLocalCacheFn,
  readCache: getLocalCacheFn,
  clearCache: clearLocalCacheFn,
  getPage,
  updatePage,
  uploadForAgent,
  httpClient: appHttpClient,
};

export default AppService;
